import React, { useState, useMemo } from 'react';
import { Filter, CheckSquare, Square, ExternalLink } from 'lucide-react';
import ComponentCard from './ComponentCard';
import { STORES } from '../data/stores';
import './ComponentList.css';

const CATEGORIES = ['All', 'Microcontroller', 'Sensor', 'Power', 'Display', 'Communication', 'Actuator', 'Passive', 'Tool', 'Other'];

export default function ComponentList({
  project,
  components,
  tools,
  tips,
  warnings,
  cartKeySet,
  onToggleCartItem,
  onAddManyToCart,
  onRemoveManyFromCart,
  cartItems,
  onSelectStoreForCart,
}) {
  const [filterCat, setFilterCat] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');

  const filtered = useMemo(() => {
    return components.filter(c => {
      if (filterCat !== 'All' && c.category !== filterCat) return false;
      if (filterPriority !== 'All' && c.priority !== filterPriority) return false;
      return true;
    });
  }, [components, filterCat, filterPriority]);

  const selectedComponents = components.filter((c) =>
    cartKeySet.has(`${project?.title || 'Untitled Project'}::${c.id}`)
  );

  const selectedCount = selectedComponents.length;
  const selectedStoreByKey = useMemo(
    () =>
      Object.fromEntries(
        (cartItems || []).map((item) => [item.key, item.selectedStoreId || null])
      ),
    [cartItems]
  );

  const selectAll = () => {
    onAddManyToCart(project, filtered);
  };

  const clearAll = () => {
    onRemoveManyFromCart(project, filtered);
  };

  const openAllInStore = (storeId) => {
    selectedComponents.forEach(comp => {
      const storeEntry = comp.stores?.find(s => s.storeId === storeId);
      const store = STORES.find(s => s.id === storeId);
      if (storeEntry && store) {
        window.open(store.searchUrl + encodeURIComponent(storeEntry.searchQuery), '_blank');
      }
    });
  };

  return (
    <div className="component-list">
      {/* Filters */}
      <div className="filters">
        <div className="filters-left">
          <Filter size={14} className="filter-icon" />
          <div className="filter-group">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`filter-btn ${filterCat === cat ? 'active' : ''}`}
                onClick={() => setFilterCat(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="filters-right">
          <select
            className="priority-select"
            value={filterPriority}
            onChange={e => setFilterPriority(e.target.value)}
          >
            <option value="All">All priorities</option>
            <option value="essential">Essential only</option>
            <option value="recommended">Recommended</option>
            <option value="optional">Optional</option>
          </select>
        </div>
      </div>

      {/* Bulk actions */}
      <div className="bulk-actions">
        <div className="bulk-left">
          <button className="bulk-btn" onClick={selectAll}>
            <CheckSquare size={14} /> Add all shown ({filtered.length})
          </button>
          {selectedCount > 0 && (
            <button className="bulk-btn clear" onClick={clearAll}>
              <Square size={14} /> Remove shown from cart
            </button>
          )}
          <span className="result-count mono">{filtered.length} components shown</span>
        </div>

        {selectedCount > 0 && (
          <div className="store-open-group">
            <span className="mono bulk-label">Open {selectedCount} cart items in:</span>
            {STORES.slice(0, 5).map(store => (
              <button
                key={store.id}
                className="store-open-btn"
                style={{ '--c': store.color }}
                onClick={() => openAllInStore(store.id)}
              >
                {store.logo} {store.name}
                <ExternalLink size={10} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Component cards */}
      <div className="cards-grid">
        {filtered.map((comp, i) => (
          <ComponentCard
            key={comp.id}
            component={comp}
            selected={cartKeySet.has(`${project?.title || 'Untitled Project'}::${comp.id}`)}
            onToggle={() => onToggleCartItem(project, comp)}
            selectedStoreId={selectedStoreByKey[`${project?.title || 'Untitled Project'}::${comp.id}`] || null}
            onSelectStore={(storeId) => onSelectStoreForCart(project, comp, storeId)}
            index={i}
          />
        ))}
      </div>

      {/* Tools section */}
      {tools && tools.length > 0 && (
        <div className="section-extra">
          <h3 className="section-extra-title">🛠 Tools Required</h3>
          <div className="tags-list">
            {tools.map((t, i) => (
              <span key={i} className="tool-tag">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Tips */}
      {tips && tips.length > 0 && (
        <div className="section-extra">
          <h3 className="section-extra-title">💡 Build Tips</h3>
          <ul className="tips-list">
            {tips.map((tip, i) => <li key={i}>{tip}</li>)}
          </ul>
        </div>
      )}

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div className="section-extra warning">
          <h3 className="section-extra-title">⚠️ Warnings</h3>
          <ul className="tips-list">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
